import {
  getAuth,
} from 'firebase/auth';
import {
  getDatabase,
  ref,
  onValue,
  onChildChanged,
  onChildAdded,
  update,
  push,
  get,
  child,
} from 'firebase/database';
import {
  all,
  takeLatest,
  takeEvery,
  put,
  call,
  select,
  take,
} from 'redux-saga/effects';
import { eventChannel, buffers } from 'redux-saga';
import { customAlphabet } from 'nanoid';
import { notification } from '../../components';
import actions from './actions';
import appActions from '../app/actions';

const slugSuffix = customAlphabet('abcdefghijklmnopqrstuvwxyz0123456789', 6);

function slugifyName(name) {
  return (name || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function buildPublicSlug(patientData) {
  const namePart = slugifyName(`${patientData?.firstName || ''} ${patientData?.lastName || ''}`.trim()) || 'orcamento';
  return `orcamento-${namePart}-${slugSuffix()}`;
}

const getMainUserFromStore = (state) => state.Auth.mainUser;

// Tracks quotation IDs whose status was changed by the professional locally,
// so the status listener can skip firing a "patient confirmed" notification for those.
const professionallyUpdatedStatusIds = new Set();

const getUnifiedTokenStore = (state) => state.Auth.unified;

const getSelectedAddressFromStore = (state) => state.App.selectedAddress;

function getUid(mainUser) {
  if (mainUser) return mainUser;
  const auth = getAuth();
  return auth.currentUser.uid;
}

function fetchPatientFromDB(patientId, addressUid, unified = {}, mainUser) {
  const db = getDatabase();
  const dbRef = ref(db);
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }
  if (unified.id && unified.address && unified.address.some((a) => a === addressUid)) {
    return get(child(dbRef, `unified/${unified.id}/patients/${patientId}`));
  }
  return get(child(dbRef, `users/${uid}/patients/${patientId}`));
}

function* fetchPatientsForQuotations(quotations, addressUid, unified, mainUser) {
  const uniquePatientIds = [...new Set(quotations.map((q) => q.patientId).filter(Boolean))];
  if (uniquePatientIds.length === 0) return {};
  const results = yield all(uniquePatientIds.map((id) => call(fetchPatientFromDB, id, addressUid, unified, mainUser)));
  const patientMap = {};
  uniquePatientIds.forEach((id, index) => {
    const val = results[index].val();
    if (val) {
      patientMap[id] = { ...val, id };
    }
  });
  return patientMap;
}

// --------------- Activity Log Helper ---------------

function* writeQuotationLog(uid, addressUid, logData) {
  try {
    const db = getDatabase();
    const logKey = push(child(ref(db), `quotations/${uid}/${addressUid}/quotationLogs`)).key;
    const actorUid = getAuth().currentUser?.uid || null;
    const logEntry = {
      id: logKey,
      actorUid,
      ...logData,
      createdAt: Date.now(),
    };
    const updates = {};
    updates[`quotations/${uid}/${addressUid}/quotationLogs/${logKey}`] = logEntry;
    yield call(() => update(ref(db), updates));
  } catch (error) {
    // Log write failure should not block the main operation
    console.warn('Failed to write quotation log:', error);
  }
}

function* fetchQuotationLogsFromDB(action) {
  try {
    yield put({ type: actions.FETCHING_QUOTATION_LOGS });
    const quotationId = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const db = getDatabase();
    const snapshot = yield call(() => get(ref(db, `quotations/${uid}/${addressUid}/quotationLogs`)));
    const val = snapshot.val();
    let logs = [];
    if (val) {
      logs = Object.keys(val)
        .map((key) => ({ ...val[key], id: key }))
        .filter((log) => !quotationId || log.quotationId === quotationId)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    yield put({ type: actions.FETCH_QUOTATION_LOGS_SUCCESS, payload: logs });
  } catch (error) {
    yield put({ type: actions.FETCH_QUOTATION_LOGS_ERROR });
  }
}

export function* fetchQuotationLogsRequest() {
  yield takeLatest(actions.FETCH_QUOTATION_LOGS_REQUEST, fetchQuotationLogsFromDB);
}

// --------------- Real-time listener ---------------

function createQuotationListener(addressUid, mainUser) {
  const db = getDatabase();
  let uid;
  if (mainUser) {
    uid = mainUser;
  } else {
    const auth = getAuth();
    const { currentUser } = auth;
    ({ uid } = currentUser);
  }
  const listener = eventChannel((emit) => {
    const quotationRef = ref(db, `quotations/${uid}/${addressUid}/quotations`);
    const unsubscribe = onValue(
      quotationRef,
      (snapshot) => {
        const val = snapshot.val();
        if (val) {
          const list = Object.keys(val)
            .map((key) => ({ ...val[key], id: key }))
            .filter((q) => !q.deletedAt);
          emit(list);
        } else {
          emit([]);
        }
      },
      () => emit([]),
    );
    return () => unsubscribe();
  }, buffers.expanding(5));
  return listener;
}

export function* fetchQuotationsRequest() {
  let currentListener = null;
  yield takeLatest(actions.FETCH_QUOTATIONS_REQUEST, function* () {
    try {
      if (currentListener) {
        currentListener.close();
        currentListener = null;
      }
      yield put({ type: actions.FETCHING_QUOTATIONS });
      const mainUser = yield select(getMainUserFromStore);
      const unified = yield select(getUnifiedTokenStore);
      let addressUid = yield select(getSelectedAddressFromStore);
      if (!addressUid) {
        yield take(appActions.SELECT_ADDRESS);
        addressUid = yield select(getSelectedAddressFromStore);
      }
      currentListener = yield call(createQuotationListener, addressUid, mainUser);
      yield takeEvery(currentListener, function* (quotations) {
        try {
          const patientMap = yield call(fetchPatientsForQuotations, quotations, addressUid, unified, mainUser);
          yield put({
            type: actions.FETCH_QUOTATIONS_SUCCESS,
            payload: quotations.map((q) => ({ ...q, patientData: patientMap[q.patientId] || null })),
          });
        } catch (_) {
          yield put({
            type: actions.FETCH_QUOTATIONS_SUCCESS,
            payload: quotations.map((q) => ({ ...q, patientData: null })),
          });
        }
      });
      yield take(appActions.CANCEL_LISTENERS);
      if (currentListener) {
        currentListener.close();
        currentListener = null;
      }
    } catch (error) {
      yield put({ type: actions.FETCH_QUOTATIONS_ERROR });
      notification('error', 'Erro ao buscar orçamentos', error.message);
    }
  });
}

// --------------- Save quotation ---------------

function getNextNumber(quotations) {
  if (!quotations || quotations.length === 0) return 1;
  const maxNumber = Math.max(...quotations.map((q) => q.number || 0));
  return maxNumber + 1;
}

function sanitizeForFirebase(obj) {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) return obj.map(sanitizeForFirebase);
  if (typeof obj === 'object') {
    const cleaned = {};
    Object.keys(obj).forEach((key) => {
      const val = obj[key];
      cleaned[key] = val === undefined ? null : sanitizeForFirebase(val);
    });
    return cleaned;
  }
  return obj;
}

function* saveQuotationOnDB(action) {
  try {
    yield put({ type: actions.SAVING_QUOTATION });
    const {
      quotation, saveAsTemplate, templateName, patientWhatsApp,
    } = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const unified = yield select(getUnifiedTokenStore);
    const creatorId = getAuth().currentUser?.uid || uid;
    const db = getDatabase();
    const now = Date.now();

    const patientWhatsAppPath = (() => {
      if (!quotation.patientId) return null;
      if (unified?.id && unified?.address?.some((a) => a === addressUid)) {
        return `unified/${unified.id}/patients/${quotation.patientId}/whatsapp`;
      }
      return `users/${uid}/patients/${quotation.patientId}/whatsapp`;
    })();
    let patientWhatsAppUpdate = null;
    if (patientWhatsApp && patientWhatsAppPath) {
      const snap = yield call(() => get(ref(db, patientWhatsAppPath)));
      if (snap.val() !== patientWhatsApp) {
        patientWhatsAppUpdate = { [patientWhatsAppPath]: patientWhatsApp };
      }
    }

    // Strip client-only fields before persisting
    const { patientData, ...quotationData } = quotation;

    if (quotation.id) {
      const publicSlug = quotation.publicSlug || buildPublicSlug(quotation.patientData);
      const updatedData = sanitizeForFirebase({
        ...quotationData,
        updatedAt: now,
        publicSlug,
      });
      const updates = {};
      updates[`quotations/${uid}/${addressUid}/quotations/${quotation.id}`] = updatedData;
      updates[`queryQuotation/${publicSlug}`] = {
        doctorId: uid,
        addressId: addressUid,
        quotationId: quotation.id,
        professional: quotationData.professionalId || null,
        creatorId: quotation.creatorId || creatorId,
        createdAt: quotation.createdAt || now,
        updatedAt: now,
      };
      if (patientWhatsAppUpdate) Object.assign(updates, patientWhatsAppUpdate);
      yield call(() => update(ref(db), updates));
      yield call(writeQuotationLog, uid, addressUid, {
        quotationId: quotation.id,
        quotationNumber: quotation.number,
        action: 'edited',
        details: { patientName: quotation.patientData ? `${quotation.patientData.firstName || ''} ${quotation.patientData.lastName || ''}`.trim() : null },
      });
    } else {
      const existingQuotations = yield select((state) => state.Quotation.quotations);
      const newKey = push(child(ref(db), `quotations/${uid}/${addressUid}/quotations`)).key;
      const newNumber = getNextNumber(existingQuotations);
      const publicSlug = buildPublicSlug(quotation.patientData);
      const newQuotation = sanitizeForFirebase({
        ...quotationData,
        id: newKey,
        number: newNumber,
        doctorId: uid,
        creatorId,
        status: quotation.status || 'draft',
        createdAt: now,
        updatedAt: now,
        sentViaWhatsApp: false,
        sentAt: null,
        publicSlug,
      });
      const updates = {};
      updates[`quotations/${uid}/${addressUid}/quotations/${newKey}`] = newQuotation;
      updates[`queryQuotation/${publicSlug}`] = {
        doctorId: uid,
        addressId: addressUid,
        quotationId: newKey,
        professional: quotationData.professionalId || null,
        creatorId,
        createdAt: now,
        updatedAt: now,
      };
      if (patientWhatsAppUpdate) Object.assign(updates, patientWhatsAppUpdate);
      yield call(() => update(ref(db), updates));
      yield call(writeQuotationLog, uid, addressUid, {
        quotationId: newKey,
        quotationNumber: newNumber,
        action: 'created',
        details: { patientName: quotation.patientData ? `${quotation.patientData.firstName || ''} ${quotation.patientData.lastName || ''}`.trim() : null },
      });
    }

    if (saveAsTemplate && templateName) {
      const templateKey = push(child(ref(db), `quotations/${uid}/quotationTemplates`)).key;
      const template = {
        id: templateKey,
        name: templateName,
        items: quotation.items || [],
        notes: quotation.notes || '',
        paymentConditions: quotation.paymentConditions || '',
        createdAt: now,
      };
      const updates = {};
      updates[`quotations/${uid}/quotationTemplates/${templateKey}`] = template;
      yield call(() => update(ref(db), updates));
    }

    yield put({ type: actions.SAVE_QUOTATION_SUCCESS });
    notification('success', 'Orçamento salvo com sucesso!');
  } catch (error) {
    yield put({ type: actions.SAVE_QUOTATION_ERROR });
    notification('error', 'Erro ao salvar orçamento', error.message);
  }
}

export function* saveQuotationRequest() {
  yield takeLatest(actions.SAVE_QUOTATION_REQUEST, saveQuotationOnDB);
}

// --------------- Reorder quotations ---------------

function* reorderQuotationsOnDB(action) {
  try {
    const orderedIds = action.payload; // [{ id, sortOrder }]
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const db = getDatabase();
    const updates = {};
    orderedIds.forEach(({ id, sortOrder }) => {
      updates[`quotations/${uid}/${addressUid}/quotations/${id}/sortOrder`] = sortOrder;
    });
    yield call(() => update(ref(db), updates));
    yield put({ type: actions.REORDER_QUOTATIONS_SUCCESS });
  } catch (error) {
    yield put({ type: actions.REORDER_QUOTATIONS_ERROR });
  }
}

export function* reorderQuotationsRequest() {
  yield takeLatest(actions.REORDER_QUOTATIONS_REQUEST, reorderQuotationsOnDB);
}

// --------------- Delete quotation (soft delete) ---------------

function* deleteQuotationOnDB(action) {
  try {
    const quotationId = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const db = getDatabase();
    const updates = {};
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/deletedAt`] = Date.now();
    yield call(() => update(ref(db), updates));
    const quotations = yield select((state) => state.Quotation.quotations);
    const deletedQ = quotations.find((q) => q.id === quotationId);
    yield call(writeQuotationLog, uid, addressUid, {
      quotationId,
      quotationNumber: deletedQ ? deletedQ.number : null,
      action: 'deleted',
      details: {},
    });
    yield put({ type: actions.DELETE_QUOTATION_SUCCESS });
    notification('success', 'Orçamento excluído com sucesso!');
  } catch (error) {
    yield put({ type: actions.DELETE_QUOTATION_ERROR });
    notification('error', 'Erro ao excluir orçamento', error.message);
  }
}

export function* deleteQuotationRequest() {
  yield takeLatest(actions.DELETE_QUOTATION_REQUEST, deleteQuotationOnDB);
}

// --------------- Update quotation status ---------------

function* updateQuotationStatusOnDB(action) {
  try {
    const { quotationId, status } = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const db = getDatabase();
    const updates = {};
    const quotations = yield select((state) => state.Quotation.quotations);
    const currentQ = quotations.find((q) => q.id === quotationId);
    const previousStatus = currentQ ? currentQ.status : null;
    professionallyUpdatedStatusIds.add(quotationId);
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/status`] = status;
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/updatedAt`] = Date.now();
    yield call(() => update(ref(db), updates));
    yield call(writeQuotationLog, uid, addressUid, {
      quotationId,
      quotationNumber: currentQ ? currentQ.number : null,
      action: 'status_changed',
      details: { from: previousStatus, to: status },
    });
    yield put({ type: actions.UPDATE_QUOTATION_STATUS_SUCCESS });
  } catch (error) {
    yield put({ type: actions.UPDATE_QUOTATION_STATUS_ERROR });
    notification('error', 'Erro ao atualizar status', error.message);
  }
}

export function* updateQuotationStatusRequest() {
  yield takeLatest(actions.UPDATE_QUOTATION_STATUS_REQUEST, updateQuotationStatusOnDB);
}

// --------------- Templates ---------------

function* fetchTemplatesFromDB() {
  try {
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const db = getDatabase();
    const snapshot = yield call(() => get(ref(db, `quotations/${uid}/quotationTemplates`)));
    const val = snapshot.val();
    let templates = [];
    if (val) {
      templates = Object.keys(val).map((key) => ({ ...val[key], id: key }));
    }
    yield put({ type: actions.FETCH_TEMPLATES_SUCCESS, payload: templates });
  } catch (error) {
    yield put({ type: actions.FETCH_TEMPLATES_ERROR });
    notification('error', 'Erro ao buscar templates', error.message);
  }
}

export function* fetchTemplatesRequest() {
  yield takeLatest(actions.FETCH_TEMPLATES_REQUEST, fetchTemplatesFromDB);
}

function* saveTemplateOnDB(action) {
  try {
    yield put({ type: actions.SAVING_TEMPLATE });
    const template = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const db = getDatabase();
    const now = Date.now();

    if (template.id) {
      const updatedTemplate = {
        ...template,
        updatedAt: now,
      };
      const updates = {};
      updates[`quotations/${uid}/quotationTemplates/${template.id}`] = updatedTemplate;
      yield call(() => update(ref(db), updates));
    } else {
      const templateKey = push(child(ref(db), `quotations/${uid}/quotationTemplates`)).key;
      const newTemplate = {
        id: templateKey,
        name: template.name,
        items: template.items || [],
        notes: template.notes || '',
        paymentConditions: template.paymentConditions || '',
        createdAt: now,
      };
      const updates = {};
      updates[`quotations/${uid}/quotationTemplates/${templateKey}`] = newTemplate;
      yield call(() => update(ref(db), updates));
    }

    yield put({ type: actions.SAVE_TEMPLATE_SUCCESS });
    yield call(fetchTemplatesFromDB);
    notification('success', 'Modelo salvo com sucesso!');
  } catch (error) {
    yield put({ type: actions.SAVE_TEMPLATE_ERROR });
    notification('error', 'Erro ao salvar modelo', error.message);
  }
}

export function* saveTemplateRequest() {
  yield takeLatest(actions.SAVE_TEMPLATE_REQUEST, saveTemplateOnDB);
}

function* deleteTemplateOnDB(action) {
  try {
    const templateId = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const db = getDatabase();
    const updates = {};
    updates[`quotations/${uid}/quotationTemplates/${templateId}`] = null;
    yield call(() => update(ref(db), updates));
    yield put({ type: actions.DELETE_TEMPLATE_SUCCESS });
    yield call(fetchTemplatesFromDB);
    notification('success', 'Template excluído com sucesso!');
  } catch (error) {
    yield put({ type: actions.DELETE_TEMPLATE_ERROR });
    notification('error', 'Erro ao excluir template', error.message);
  }
}

export function* deleteTemplateRequest() {
  yield takeLatest(actions.DELETE_TEMPLATE_REQUEST, deleteTemplateOnDB);
}

// --------------- Public quotation ---------------

function* fetchPublicQuotationFromDB(action) {
  try {
    yield put({ type: actions.FETCHING_PUBLIC_QUOTATION });
    const { quotationId, doctorId, addressId } = action.payload;
    const db = getDatabase();
    const snapshot = yield call(() => get(ref(db, `quotations/${doctorId}/${addressId}/quotations/${quotationId}`)));
    const val = snapshot.val();
    if (val && !val.deletedAt) {
      yield put({
        type: actions.FETCH_PUBLIC_QUOTATION_SUCCESS,
        payload: { ...val, id: quotationId },
      });
    } else {
      yield put({ type: actions.FETCH_PUBLIC_QUOTATION_ERROR });
    }
  } catch (error) {
    yield put({ type: actions.FETCH_PUBLIC_QUOTATION_ERROR });
  }
}

export function* fetchPublicQuotationRequest() {
  yield takeLatest(actions.FETCH_PUBLIC_QUOTATION_REQUEST, fetchPublicQuotationFromDB);
}

// --------------- Send WhatsApp ---------------

function* sendWhatsAppOnDB(action) {
  try {
    const { quotationId, phone, messageText } = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const db = getDatabase();
    const now = Date.now();
    const updates = {};
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/sentViaWhatsApp`] = true;
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/sentAt`] = now;
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/status`] = 'sent';
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/updatedAt`] = now;
    yield call(() => update(ref(db), updates));

    const quotations = yield select((state) => state.Quotation.quotations);
    const sentQuotation = quotations.find((q) => q.id === quotationId);
    const publicUrl = `${window.location.origin}/orcamento-publico/${sentQuotation?.publicSlug}`;
    const finalText = messageText || `Olá, segue seu orçamento: ${publicUrl}`;
    const message = encodeURIComponent(finalText);
    const cleanPhone = phone.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${message}`;
    window.open(whatsappUrl, '_blank');

    yield call(writeQuotationLog, uid, addressUid, {
      quotationId,
      quotationNumber: sentQuotation ? sentQuotation.number : null,
      action: 'whatsapp_sent',
      details: { phone: cleanPhone },
    });
    yield put({ type: actions.SEND_WHATSAPP_SUCCESS });
    notification('success', 'Orçamento enviado via WhatsApp!');
  } catch (error) {
    yield put({ type: actions.SEND_WHATSAPP_ERROR });
    notification('error', 'Erro ao enviar via WhatsApp', error.message);
  }
}

export function* sendWhatsAppRequest() {
  yield takeLatest(actions.SEND_WHATSAPP_REQUEST, sendWhatsAppOnDB);
}

// --------------- Quotation Tags ---------------

function* fetchQuotationTagsFromDB() {
  try {
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const db = getDatabase();
    const snapshot = yield call(() => get(ref(db, `quotations/${uid}/quotationTags`)));
    const val = snapshot.val();
    let tags = [];
    if (val) {
      tags = Object.keys(val)
        .map((key) => ({ ...val[key], id: key }))
        .filter((t) => !t.deletedAt);
    }
    yield put({ type: actions.FETCH_QUOTATION_TAGS_SUCCESS, payload: tags });
  } catch (error) {
    yield put({ type: actions.FETCH_QUOTATION_TAGS_ERROR });
    notification('error', 'Erro ao buscar tags', error.message);
  }
}

export function* fetchQuotationTagsRequest() {
  yield takeLatest(actions.FETCH_QUOTATION_TAGS_REQUEST, fetchQuotationTagsFromDB);
}

function* saveQuotationTagOnDB(action) {
  try {
    yield put({ type: actions.SAVING_QUOTATION_TAG });
    const tag = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const db = getDatabase();
    const now = Date.now();

    if (tag.id) {
      const updatedTag = { ...tag, updatedAt: now };
      const updates = {};
      updates[`quotations/${uid}/quotationTags/${tag.id}`] = updatedTag;
      yield call(() => update(ref(db), updates));
    } else {
      const tagKey = push(child(ref(db), `quotations/${uid}/quotationTags`)).key;
      const newTag = {
        id: tagKey,
        name: tag.name,
        color: tag.color || '#1890ff',
        createdAt: now,
      };
      const updates = {};
      updates[`quotations/${uid}/quotationTags/${tagKey}`] = newTag;
      yield call(() => update(ref(db), updates));
    }

    yield put({ type: actions.SAVE_QUOTATION_TAG_SUCCESS });
    yield call(fetchQuotationTagsFromDB);
    notification('success', 'Tag salva com sucesso!');
  } catch (error) {
    yield put({ type: actions.SAVE_QUOTATION_TAG_ERROR });
    notification('error', 'Erro ao salvar tag', error.message);
  }
}

export function* saveQuotationTagRequest() {
  yield takeLatest(actions.SAVE_QUOTATION_TAG_REQUEST, saveQuotationTagOnDB);
}

function* deleteQuotationTagOnDB(action) {
  try {
    const tagId = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const db = getDatabase();
    const updates = {};
    updates[`quotations/${uid}/quotationTags/${tagId}/deletedAt`] = Date.now();
    yield call(() => update(ref(db), updates));
    yield put({ type: actions.DELETE_QUOTATION_TAG_SUCCESS });
    yield call(fetchQuotationTagsFromDB);
    notification('success', 'Tag excluída com sucesso!');
  } catch (error) {
    yield put({ type: actions.DELETE_QUOTATION_TAG_ERROR });
    notification('error', 'Erro ao excluir tag', error.message);
  }
}

export function* deleteQuotationTagRequest() {
  yield takeLatest(actions.DELETE_QUOTATION_TAG_REQUEST, deleteQuotationTagOnDB);
}

// --------------- Update quotation tags inline ---------------

function* updateQuotationTagsOnDB(action) {
  try {
    const { quotationId, tags } = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const db = getDatabase();
    const updates = {};
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/tags`] = tags;
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/updatedAt`] = Date.now();
    yield call(() => update(ref(db), updates));
    yield put({ type: actions.UPDATE_QUOTATION_TAGS_SUCCESS });
  } catch (error) {
    yield put({ type: actions.UPDATE_QUOTATION_TAGS_ERROR });
    notification('error', 'Erro ao atualizar tags', error.message);
  }
}

export function* updateQuotationTagsRequest() {
  yield takeLatest(actions.UPDATE_QUOTATION_TAGS_REQUEST, updateQuotationTagsOnDB);
}

// --------------- Execute quotation ---------------

function* executeQuotationOnDB(action) {
  try {
    const { quotationId, appointmentId } = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const db = getDatabase();
    const now = Date.now();
    const updates = {};
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/status`] = 'executed';
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/appointmentId`] = appointmentId;
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/executedAt`] = now;
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/updatedAt`] = now;
    yield call(() => update(ref(db), updates));

    const quotations = yield select((state) => state.Quotation.quotations);
    const currentQ = quotations.find((q) => q.id === quotationId);
    yield call(writeQuotationLog, uid, addressUid, {
      quotationId,
      quotationNumber: currentQ ? currentQ.number : null,
      action: 'executed',
      details: { appointmentId },
    });
    yield put({ type: actions.EXECUTE_QUOTATION_SUCCESS });
    notification('success', 'Orçamento executado com sucesso!');
  } catch (error) {
    yield put({ type: actions.EXECUTE_QUOTATION_ERROR });
    notification('error', 'Erro ao executar orçamento', error.message);
  }
}

export function* executeQuotationRequest() {
  yield takeLatest(actions.EXECUTE_QUOTATION_REQUEST, executeQuotationOnDB);
}

// --------------- Quotation Real-time Listeners (comments + status) ---------------

function createQuotationCommentsListener(uid, addressUid) {
  const db = getDatabase();
  const commentsRef = ref(db, `quotations/${uid}/${addressUid}/quotationComments`);
  return eventChannel((emit) => {
    const unsubscribe = onValue(commentsRef, (snapshot) => {
      const val = snapshot.val();
      emit(val
        ? Object.keys(val).map((key) => ({ ...val[key], id: key })).filter((c) => !c.deletedAt)
        : []);
    });
    return () => unsubscribe();
  }, buffers.expanding(5));
}

function createQuotationStatusListener(uid, addressUid) {
  const db = getDatabase();
  const quotationsRef = ref(db, `quotations/${uid}/${addressUid}/quotations`);
  return eventChannel((emit) => {
    const unsubscribe = onChildChanged(quotationsRef, (snapshot) => {
      emit({ id: snapshot.key, ...snapshot.val() });
    });
    return () => unsubscribe();
  }, buffers.expanding(5));
}

function createUserMentionsListener(uid, addressUid, currentUserId) {
  const db = getDatabase();
  const mentionsRef = ref(db, `quotations/${uid}/${addressUid}/userMentions/${currentUserId}`);
  return eventChannel((emit) => {
    const unsubscribe = onChildAdded(mentionsRef, (snapshot) => {
      emit({ id: snapshot.key, ...snapshot.val() });
    }, (error) => {
      console.warn('[UserMentions] subscribe error', error);
    });
    return () => unsubscribe();
  }, buffers.expanding(10));
}

export function* quotationListenersRequest() {
  let commentsChannel = null;
  let statusChannel = null;
  let mentionsChannel = null;
  yield takeLatest(actions.QUOTATION_LISTENERS_START, function* () {
    try {
      if (commentsChannel) { commentsChannel.close(); commentsChannel = null; }
      if (statusChannel) { statusChannel.close(); statusChannel = null; }
      if (mentionsChannel) { mentionsChannel.close(); mentionsChannel = null; }

      const mainUser = yield select(getMainUserFromStore);
      const uid = getUid(mainUser);
      const addressUid = yield select(getSelectedAddressFromStore);
      const currentUserId = getAuth().currentUser?.uid;

      commentsChannel = yield call(createQuotationCommentsListener, uid, addressUid);
      statusChannel = yield call(createQuotationStatusListener, uid, addressUid);
      if (currentUserId) {
        mentionsChannel = yield call(createUserMentionsListener, uid, addressUid, currentUserId);
      }

      yield takeEvery(commentsChannel, function* (comments) {
        yield put({ type: actions.QUOTATION_COMMENTS_LISTENER_SUCCESS, payload: comments });
        const currentQuotation = yield select((state) => state.Quotation.currentQuotation);
        if (currentQuotation) {
          const filtered = comments
            .filter((c) => c.quotationId === currentQuotation.id)
            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          yield put({ type: actions.FETCH_QUOTATION_COMMENTS_SUCCESS, payload: filtered });
        }
      });

      yield takeEvery(statusChannel, function* (quotation) {
        if (quotation.status === 'approved' || quotation.status === 'rejected') {
          if (professionallyUpdatedStatusIds.has(quotation.id)) {
            professionallyUpdatedStatusIds.delete(quotation.id);
            return;
          }
          yield put({ type: actions.QUOTATION_STATUS_CHANGE, payload: quotation });
        }
      });

      if (mentionsChannel) {
        const subscribeTime = Date.now();
        yield takeEvery(mentionsChannel, function* (mention) {
          // Tag whether this is a fresh write (worth toasting) or a backfill replay
          // from `onChildAdded` walking pre-existing rows.
          const isNew = (mention.createdAt || 0) > (subscribeTime - 2000);
          yield put({
            type: actions.USER_MENTION_RECEIVED,
            payload: { ...mention, isNew },
          });
        });
      } else {
        console.warn('[UserMentions] mentionsChannel is null — currentUserId was not set at listener start');
      }

      yield take(appActions.CANCEL_LISTENERS);
      if (commentsChannel) { commentsChannel.close(); commentsChannel = null; }
      if (statusChannel) { statusChannel.close(); statusChannel = null; }
      if (mentionsChannel) { mentionsChannel.close(); mentionsChannel = null; }
    } catch (error) {
      console.warn('[QuotationListeners] error', error);
    }
  });
}

// --------------- Quotation Comments ---------------

function* fetchQuotationCommentsFromDB(action) {
  try {
    yield put({ type: actions.FETCHING_QUOTATION_COMMENTS });
    const quotationId = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const db = getDatabase();
    const snapshot = yield call(() => get(ref(db, `quotations/${uid}/${addressUid}/quotationComments`)));
    const val = snapshot.val();
    let comments = [];
    if (val) {
      comments = Object.keys(val)
        .map((key) => ({ ...val[key], id: key }))
        .filter((c) => c.quotationId === quotationId && !c.deletedAt)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    yield put({ type: actions.FETCH_QUOTATION_COMMENTS_SUCCESS, payload: comments });
  } catch (error) {
    yield put({ type: actions.FETCH_QUOTATION_COMMENTS_ERROR });
  }
}

export function* fetchQuotationCommentsRequest() {
  yield takeLatest(actions.FETCH_QUOTATION_COMMENTS_REQUEST, fetchQuotationCommentsFromDB);
}

function* saveQuotationCommentOnDB(action) {
  try {
    yield put({ type: actions.SAVING_QUOTATION_COMMENT });
    const comment = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const currentUserId = getAuth().currentUser?.uid;
    const db = getDatabase();
    const now = Date.now();
    const updates = {};

    const newMentioned = comment.mentionedUserIds || [];
    let commentKey;
    let oldMentioned = [];

    if (comment.id) {
      commentKey = comment.id;
      const oldSnap = yield call(() => get(ref(db, `quotations/${uid}/${addressUid}/quotationComments/${comment.id}`)));
      oldMentioned = oldSnap.val()?.mentionedUserIds || [];
      updates[`quotations/${uid}/${addressUid}/quotationComments/${comment.id}/text`] = comment.text;
      updates[`quotations/${uid}/${addressUid}/quotationComments/${comment.id}/mentionedUserIds`] = newMentioned;
      updates[`quotations/${uid}/${addressUid}/quotationComments/${comment.id}/updatedAt`] = now;
    } else {
      commentKey = push(child(ref(db), `quotations/${uid}/${addressUid}/quotationComments`)).key;
      updates[`quotations/${uid}/${addressUid}/quotationComments/${commentKey}`] = {
        id: commentKey,
        quotationId: comment.quotationId,
        text: comment.text,
        authorName: comment.authorName || '',
        authorUid: currentUserId || uid,
        createdAt: now,
        mentionedUserIds: newMentioned,
      };
    }

    // Fan-out / cleanup rows in userMentions/<targetId>/<commentId>.
    const removedTargets = oldMentioned.filter((id) => !newMentioned.includes(id));
    const addedTargets = newMentioned.filter((id) => !oldMentioned.includes(id) && id !== currentUserId);

    removedTargets.forEach((targetId) => {
      updates[`quotations/${uid}/${addressUid}/userMentions/${targetId}/${commentKey}`] = null;
    });
    addedTargets.forEach((targetId) => {
      updates[`quotations/${uid}/${addressUid}/userMentions/${targetId}/${commentKey}`] = {
        commentId: commentKey,
        quotationId: comment.quotationId,
        authorName: comment.authorName || '',
        authorUid: currentUserId || uid,
        snippet: (comment.text || '').slice(0, 120),
        createdAt: now,
      };
    });

    yield call(() => update(ref(db), updates));
    yield put({ type: actions.SAVE_QUOTATION_COMMENT_SUCCESS });
    yield call(fetchQuotationCommentsFromDB, { payload: comment.quotationId });
  } catch (error) {
    yield put({ type: actions.SAVE_QUOTATION_COMMENT_ERROR });
    notification('error', 'Erro ao salvar comentário', error.message);
  }
}

export function* saveQuotationCommentRequest() {
  yield takeLatest(actions.SAVE_QUOTATION_COMMENT_REQUEST, saveQuotationCommentOnDB);
}

function* deleteQuotationCommentOnDB(action) {
  try {
    const { commentId, quotationId } = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const db = getDatabase();

    const snap = yield call(() => get(ref(db, `quotations/${uid}/${addressUid}/quotationComments/${commentId}`)));
    const mentioned = snap.val()?.mentionedUserIds || [];

    const updates = {};
    updates[`quotations/${uid}/${addressUid}/quotationComments/${commentId}/deletedAt`] = Date.now();
    mentioned.forEach((targetId) => {
      updates[`quotations/${uid}/${addressUid}/userMentions/${targetId}/${commentId}`] = null;
    });

    yield call(() => update(ref(db), updates));
    yield put({ type: actions.DELETE_QUOTATION_COMMENT_SUCCESS });
    yield call(fetchQuotationCommentsFromDB, { payload: quotationId });
  } catch (error) {
    yield put({ type: actions.DELETE_QUOTATION_COMMENT_ERROR });
    notification('error', 'Erro ao excluir comentário', error.message);
  }
}

export function* deleteQuotationCommentRequest() {
  yield takeLatest(actions.DELETE_QUOTATION_COMMENT_REQUEST, deleteQuotationCommentOnDB);
}

function* markQuotationSeenByMentionedUserOnDB(action) {
  try {
    const quotationId = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const currentUserUid = getAuth().currentUser?.uid;
    if (!currentUserUid) return;
    const db = getDatabase();
    const updates = {};
    updates[`quotations/${uid}/${addressUid}/quotations/${quotationId}/seenByMentionedUsers/${currentUserUid}`] = true;
    yield call(() => update(ref(db), updates));
  } catch (error) {
    console.warn('Failed to mark quotation seen by mentioned user:', error);
  }
}

export function* markQuotationSeenByMentionedUserRequest() {
  yield takeLatest(actions.MARK_QUOTATION_SEEN_BY_MENTIONED_USER, markQuotationSeenByMentionedUserOnDB);
}

function* dismissUserMentionOnDB(action) {
  try {
    const mentionId = action.payload;
    const mainUser = yield select(getMainUserFromStore);
    const uid = getUid(mainUser);
    const addressUid = yield select(getSelectedAddressFromStore);
    const currentUserId = getAuth().currentUser?.uid;
    if (!currentUserId || !mentionId) return;
    const db = getDatabase();
    yield call(() => update(ref(db), {
      [`quotations/${uid}/${addressUid}/userMentions/${currentUserId}/${mentionId}`]: null,
    }));
  } catch (error) {
    console.warn('[UserMentions] dismiss error', error);
  }
}

export function* dismissUserMentionRequest() {
  yield takeEvery(actions.DISMISS_USER_MENTION_REQUEST, dismissUserMentionOnDB);
}

export default function* quotationSaga() {
  yield all([
    fetchQuotationsRequest(),
    saveQuotationRequest(),
    deleteQuotationRequest(),
    updateQuotationStatusRequest(),
    fetchTemplatesRequest(),
    saveTemplateRequest(),
    deleteTemplateRequest(),
    fetchPublicQuotationRequest(),
    sendWhatsAppRequest(),
    fetchQuotationTagsRequest(),
    saveQuotationTagRequest(),
    deleteQuotationTagRequest(),
    updateQuotationTagsRequest(),
    fetchQuotationLogsRequest(),
    reorderQuotationsRequest(),
    executeQuotationRequest(),
    fetchQuotationCommentsRequest(),
    saveQuotationCommentRequest(),
    deleteQuotationCommentRequest(),
    quotationListenersRequest(),
    markQuotationSeenByMentionedUserRequest(),
    dismissUserMentionRequest(),
  ]);
}
